/**
 * 搜索配置页面
 * 用户可配置搜索引擎、搜索深度、结果数、API Key
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSearchConfig, updateSearchConfig, deleteSearchConfig, SearchConfig } from '../services/ragApi';
import { toast, confirm } from '../store/toastStore';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';

interface FormValues {
  preferred_engine: 'bing' | 'duckduckgo' | 'both';
  default_search_depth: 'quick' | 'normal' | 'deep';
  default_max_results: number;
}

const FALLBACK_VALUES: FormValues = {
  preferred_engine: 'bing',
  default_search_depth: 'normal',
  default_max_results: 5,
};

const SearchConfigPage: React.FC = () => {
  const [form, setForm] = useState<FormValues>({ ...FALLBACK_VALUES });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');

  // 后端返回的配置（脏检测基线）
  const originalRef = useRef<{ form: FormValues; apiKey: string }>({
    form: { ...FALLBACK_VALUES },
    apiKey: '',
  });
  // 用户是否有已有配置（控制还原按钮，仅在配了 API Key 时为真）
  const [hasCustomConfig, setHasCustomConfig] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const config = await getSearchConfig();
      const vals: FormValues = {
        preferred_engine: config.preferred_engine || 'bing',
        default_search_depth: config.default_search_depth || 'normal',
        default_max_results: config.default_max_results || 5,
      };
      setForm(vals);
      originalRef.current = { form: { ...vals }, apiKey: '' };
      setApiKeyInput('');

      // 判断用户是否有自定义配置（仅当配了 API Key 时才显示还原按钮）
      setHasCustomConfig(config.bing_api_key_configured);
    } catch (e) {
      console.error('加载搜索配置失败', e);
    } finally {
      setLoading(false);
    }
  };

  // 是否表单有修改
  const isDirty = useCallback(() => {
    const orig = originalRef.current;
    return form.preferred_engine !== orig.form.preferred_engine
      || form.default_search_depth !== orig.form.default_search_depth
      || form.default_max_results !== orig.form.default_max_results
      || apiKeyInput !== orig.apiKey;
  }, [form, apiKeyInput]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData: any = { ...form };
      // 只传入有改动的字段（patch 语义）
      const orig = originalRef.current;
      if (apiKeyInput !== orig.apiKey) {
        updateData.bing_api_key = apiKeyInput;
      }
      await updateSearchConfig(updateData);
      // 更新原始值为当前值
      originalRef.current = { form: { ...form }, apiKey: apiKeyInput };
      setHasCustomConfig(true);
    } catch (e) {
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!await confirm({ title: '还原确认', message: '确认还原为系统默认值？这将清除你的所有自定义搜索配置。', confirmLabel: '还原', danger: true })) return;
    setResetting(true);
    try {
      await deleteSearchConfig();
      // 从后端读取真实的系统默认值（管理员可能在后台改过默认搜索引擎等）
      const config = await getSearchConfig();
      const vals: FormValues = {
        preferred_engine: config.preferred_engine || 'bing',
        default_search_depth: config.default_search_depth || 'normal',
        default_max_results: config.default_max_results || 5,
      };
      setForm(vals);
      setApiKeyInput('');
      originalRef.current = { form: { ...vals }, apiKey: '' };
      setHasCustomConfig(false);
    } catch (e) {
      toast.error('还原失败');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto py-8 text-muted-foreground">加载中...</div>;

  return (
    <div className="max-w-2xl mx-auto py-6">
      <PageHeader title="搜索配置" />

      <div className="bg-white rounded-xl border border-border p-6 space-y-6">
        {/* 首选搜索引擎 */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">首选搜索引擎</label>
          <div className="space-y-2">
            {([
              { value: 'bing', label: 'Bing', desc: '微软 Bing 搜索，需要配置 API Key' },
              { value: 'duckduckgo', label: 'DuckDuckGo', desc: '免费使用，无需 API Key' },
              { value: 'both', label: '自动（Bing + 备用）', desc: '优先用 Bing，失败时降级 DuckDuckGo' },
            ] as const).map(opt => (
              <label key={opt.value} className={`flex items-center p-3 border rounded-lg cursor-pointer ${form.preferred_engine === opt.value ? 'border-blue-500 bg-primary/10' : 'border-border'}`}>
                <input type="radio" name="engine" value={opt.value}
                  checked={form.preferred_engine === opt.value}
                  onChange={() => setForm(f => ({ ...f, preferred_engine: opt.value }))}
                  className="w-4 h-4 text-primary mr-3" />
                <div>
                  <div className="text-sm font-medium text-foreground">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* 默认搜索深度 */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">默认搜索深度</label>
          <select
            value={form.default_search_depth}
            onChange={e => setForm(f => ({ ...f, default_search_depth: e.target.value as any }))}
            className="w-full border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="quick">快速（3 个结果，~30秒）</option>
            <option value="normal">标准（5 个结果，~1分钟）</option>
            <option value="deep">深度（10 个结果，~2分钟）</option>
          </select>
        </div>

        {/* 默认搜索结果数 */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            默认搜索结果数
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="1" max="20"
              value={form.default_max_results}
              onChange={e => setForm(f => ({ ...f, default_max_results: parseInt(e.target.value) }))}
              className="flex-1"
            />
            <span className="text-sm font-mono w-8 text-right">{form.default_max_results}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">每轮搜索最多获取的结果数（1-20）</p>
        </div>

        {/* API 密钥 */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Bing Search API Key
          </label>
          <div className="flex gap-2">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
              placeholder="留空则使用系统全局 API Key"
              className="flex-1 border border-border rounded-lg px-3 py-2 text-sm font-mono"
            />
            <button onClick={() => setShowApiKey(!showApiKey)}
              className="px-3 py-2 text-sm text-foreground hover:text-foreground border border-border rounded-lg">
              {showApiKey ? '隐藏' : '显示'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            留空则使用系统全局 API Key（若无则降级为 DuckDuckGo）
          </p>
        </div>

        {/* 操作按钮 */}
        <div className="pt-4 border-t border-border flex items-center justify-between">
          {/* 还原按钮 */}
          <button onClick={handleReset}
            disabled={!hasCustomConfig || resetting}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors ${hasCustomConfig && !resetting
              ? 'border-red-200 text-red-600 hover:bg-red-50'
              : 'border-border text-muted-foreground cursor-not-allowed'}`}>
            {resetting ? '还原中...' : '还原默认值'}
          </button>

          {/* 保存按钮 */}
          <button onClick={handleSave}
            disabled={!isDirty() || saving}
            className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${isDirty() && !saving
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-accent text-muted-foreground cursor-not-allowed'}`}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        <InformationCircleIcon className="w-5 h-5 inline-block" /> 未配置时使用系统默认值。搜索深度和结果数可在「搜索配置」页面随时调整。
      </p>
    </div>
  );
};

export default SearchConfigPage;
